import {
    ref, computed, watch, Ref
} from 'vue';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { useTaskDefinitionsStore } from '@agyletime/vue-3-common/src/composables/taskDefinitionsStore';
import { fetchAssignmentAgeBandsByWorkloads } from '@/pages/OperationsInsights/services/fetchAssignmentAgeBandsByWorkloads';
import {
    ApiRespAssignmentAgeBandsByWorkload,
    AssignmentAgeBandByWorkload
} from '@/pages/OperationsInsights/types/AssignmentMetricsResponse';
import { useWorkloadAssignmentConfigurationsStore } from '@/stores/workloadAssignmentConfigurationsStore';
import OpenAssignmentAgeBandByWorkloadCellRenderer from '@/pages/agGridUtils/OpenAssignmentAgeBandByWorkloadCellRenderer.vue';
const { taskDefinitionsMap } = useTaskDefinitionsStore();
const { assignmentAgeBandConfigNamesStore } = useWorkloadAssignmentConfigurationsStore();
type WorkloadChartSeriesData = {
    type: 'bar';
    name: string;
    data: number[];
}[];

type WorkloadCategoriesAndSeriesData = {
    xAxisCategories: string[];
    chartSeries: WorkloadChartSeriesData;
};
export function useAssignmentsAgeBandsWorkloadsData(queryParams: Ref<ApiQueryParams>) {
    const logger = useLogger('useBLAgeBandsWorkloadsData');
    const ageBandsDataByWorkload = ref<ApiRespAssignmentAgeBandsByWorkload | null>(null);

    const isLoading = ref(false);
    const error = ref<string | null>(null);

    const workloadChartSeries = computed((): WorkloadCategoriesAndSeriesData => {
        if (!ageBandsDataByWorkload.value) {
            return { xAxisCategories: [], chartSeries: [] };
        }

        // Build categories (workload names for y-axis)
        const categories: string[] = [];
        for (const workloadMetrics of ageBandsDataByWorkload.value.assignmentAgeBandsByWorkloads) {
            const name = taskDefinitionsMap.get(workloadMetrics.workloadId)?.name ?? `Unknown (${workloadMetrics.workloadId})`;
            categories.push(name);
        }

        // Get all age band names
        const ageBandNames = extractAssignmentAgeBandNames(ageBandsDataByWorkload.value.assignmentAgeBandsByWorkloads);

        // Create series for each age band
        const chartSeries: WorkloadChartSeriesData = ageBandNames.map((ageBandName) => {
            const data = ageBandsDataByWorkload.value!.assignmentAgeBandsByWorkloads.map((workloadMetrics) => {
                const band = workloadMetrics.assignmentAgeBands.find((b) => b.ageBandName === ageBandName);
                return band ? band.openCountEoP : 0;
            });

            return {
                type: 'bar',
                name: ageBandName,
                data
            };
        });

        return { xAxisCategories: categories, chartSeries };
    });

    const extractAssignmentAgeBandNames = (assignmentAgeBandsByWorkloads: AssignmentAgeBandByWorkload[]) => {
        const ageBandNames = new Set<string>();
        assignmentAgeBandConfigNamesStore.data.forEach((i) => ageBandNames.add(i));
        for (const assignmentAgeBandsByWorkload of assignmentAgeBandsByWorkloads) {
            for (const band of assignmentAgeBandsByWorkload.assignmentAgeBands) {
                ageBandNames.add(band.ageBandName);
            }
        }
        return Array.from(ageBandNames);
    };

    const AGE_BAND_EOP_SUFFIX = '__NetChange';
    const generateAgeBandLastIntervalColName = (ageBandName: string) => `${ageBandName}${AGE_BAND_EOP_SUFFIX}`;

    // Grid Data - Helper function to transform Backlog AgeBand Buckets ByWorkstream to grid data
    const transformAssignmentAgeBandByWorkloadToGridData = (assignmentAgeBandsByWorkloads: AssignmentAgeBandByWorkload[]) => {
        const gridMap = new Map<number, object>();
        for (const assignmentAgeBandsByWorkload of assignmentAgeBandsByWorkloads) {
            const name = taskDefinitionsMap.get(assignmentAgeBandsByWorkload.workloadId)?.name ?? `Unknown (${assignmentAgeBandsByWorkload.workloadId})`;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const row: any = { name };

            let totalOpenByWorkload = 0;
            for (const ageBandBucketsByAgeBandName of assignmentAgeBandsByWorkload.assignmentAgeBands) {
                const { ageBandName } = ageBandBucketsByAgeBandName;
                row[ageBandName] = ageBandBucketsByAgeBandName.openCountEoP;
                totalOpenByWorkload += ageBandBucketsByAgeBandName.openCountEoP ?? 0;
                // Tracking to display __NetChange
                row[generateAgeBandLastIntervalColName(ageBandName)] = ageBandBucketsByAgeBandName.openCountNetChanges;
            }

            row['total'] = totalOpenByWorkload;
            gridMap.set(assignmentAgeBandsByWorkload.workloadId, row);
        }
        return Array.from(gridMap.values());
    };

    const assignmentAgeByWorkloadColumnDefs = computed(() => {
        const logger = useLogger('assignment-age-workload--race-condition');

        if (!ageBandsDataByWorkload.value) return [];
        const ageBandNames = extractAssignmentAgeBandNames(ageBandsDataByWorkload.value.assignmentAgeBandsByWorkloads);

        logger.info('assignmentAgeByWorkloadColumnDefs');

        return [
            {
                field: 'name', headerName: 'Workload', filter: true, flex: 2
            },
            ...ageBandNames.map((ageBandName) => {
                return {
                    field: ageBandName,
                    headerName: ageBandName,
                    dataType: 'number',
                    filter: true,
                    cellRenderer: OpenAssignmentAgeBandByWorkloadCellRenderer,
                    flex: 1
                };
            }),
            {
                field: 'total', headerName: 'Total', filter: true, flex: 1
            }
        ];
    });

    const assignmentAgeByWorkloadGridData = computed(() => {
        if (!ageBandsDataByWorkload.value) return [];
        return transformAssignmentAgeBandByWorkloadToGridData(ageBandsDataByWorkload.value.assignmentAgeBandsByWorkloads);
    });

    const fetchData = async () => {
        if (!queryParams.value) return;
        if (!queryParams.value.workstreamDefinitionIds?.length) return;

        isLoading.value = true;
        error.value = null;

        try {
            const ageBandsByWorkload = await fetchAssignmentAgeBandsByWorkloads(queryParams.value);
            logger.debug('ageBandsByWorkload', { ageBandsByWorkload });
            ageBandsDataByWorkload.value = ageBandsByWorkload;
        } catch (err) {
            error.value = 'Failed to fetch backlog age band data by workloads';
            logger.error('Failed to fetch backlog age band data by workloads', { error: err });
        } finally {
            isLoading.value = false;
        }
    };

    watch(queryParams, fetchData, { immediate: true, deep: true });

    return {
        workloadChartSeries,
        assignmentAgeByWorkloadColumnDefs,
        assignmentAgeByWorkloadGridData,
        isLoading,
        error,
        fetchData
    };
}
